import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, Trash2, CheckCircle, ScrollText,
  BarChart3, ChevronLeft, ChevronRight, Navigation, MapPin, Clock
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../hooks/useAuth";
import { supabase } from "../../lib/supabaseClient";

const NAV = [
  { label: "Dashboard",     icon: LayoutDashboard, path: "/hoa/dashboard",    active: true },
  { label: "Submissions",   icon: Trash2,           path: "/hoa/submissions" },
  { label: "Valet Status",  icon: CheckCircle,      path: "/hoa/valet-status" },
  { label: "Activity Logs", icon: ScrollText,       path: "/hoa/logs" },
  { label: "Reports",       icon: BarChart3,        path: "/hoa/reports" },
];

function getToday() { return new Date().toISOString().split("T")[0]; }
// function addDays(dateStr, n) {
//   const d = new Date(dateStr + "T00:00:00");
//   d.setDate(d.getDate() + n);
//   return d.toISOString().split("T")[0];
// }
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d); // local time, no timezone shift
  date.setDate(date.getDate() + n);
  const yy = date.getFullYear();
  const mm  = String(date.getMonth() + 1).padStart(2, "0");
  const dd  = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
function formatDate(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const STATUS_STEPS = [
  { key: "on_the_way", label: "On The Way",      desc: "Valet heading to property",         color: "#ff9800", bg: "#fff3e0", border: "#ffb74d", icon: Navigation },
  { key: "on_site",    label: "Arrived On-Site", desc: "Valet at property, service active", color: "#1565c0", bg: "#e3f2fd", border: "#90caf9", icon: MapPin },
  { key: "completed",  label: "Completed",        desc: "Service done for the day",          color: "var(--g600)", bg: "var(--g50)", border: "var(--g200)", icon: CheckCircle },
];

function getStatusConfig(status) {
  return STATUS_STEPS.find(s => s.key === status) || null;
}

function DateNavigator({ date, onChange }) {
  const isToday = date === getToday();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button onClick={() => onChange(addDays(date, -1))} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ChevronLeft size={16} color="var(--mut)" />
      </button>
      <div style={{ padding: "6px 16px", borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", fontSize: 13, fontWeight: 600, color: "var(--ink)", minWidth: 150, textAlign: "center" }}>
        {isToday ? `Today · ${formatDate(date)}` : formatDate(date)}
      </div>
      <button onClick={() => onChange(addDays(date, 1))} disabled={isToday} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: isToday ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: isToday ? 0.4 : 1 }}>
        <ChevronRight size={16} color="var(--mut)" />
      </button>
      {!isToday && (
        <button onClick={() => onChange(getToday())} style={{ padding: "6px 12px", borderRadius: 8, border: "1.5px solid var(--g300)", background: "var(--g50)", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "var(--g600)" }}>Today</button>
      )}
    </div>
  );
}

export default function HOAValetStatus() {
  const { profile } = useAuth();
  const propertyId  = profile?.property_id;
  const today       = getToday();

  const [selectedDate,  setSelectedDate]  = useState(today);
  const [valetRecord,   setValetRecord]   = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loading,       setLoading]       = useState(true);

  const fetchData = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);

    // Current valet status for selected date
    const { data: vs } = await supabase
      .from("valet_status")
      .select("status, updated_at, valet_code_id")
      .eq("property_id", propertyId)
      .eq("service_date", selectedDate)
      .maybeSingle();
    setValetRecord(vs || null);

    // Status change history from activity_logs
    const { data: history } = await supabase
      .from("activity_logs")
      .select("id, metadata, created_at")
      .eq("property_id", propertyId)
      .eq("action", "valet_status_changed")
      .gte("created_at", `${selectedDate}T00:00:00`)
      .lte("created_at", `${selectedDate}T23:59:59`)
      .order("created_at", { ascending: true });
    setStatusHistory(history || []);

    setLoading(false);
  }, [propertyId, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Realtime
  useEffect(() => {
    if (!propertyId) return;
    const channel = supabase.channel("hoa_vs_page")
      .on("postgres_changes", { event: "*", schema: "public", table: "valet_status", filter: `property_id=eq.${propertyId}` }, fetchData)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [propertyId, fetchData]);

  const currentStatus = valetRecord?.status || null;
  const currentIdx    = STATUS_STEPS.findIndex(s => s.key === currentStatus);
  const isToday       = selectedDate === today;

  return (
    <DashboardLayout role="hoa_admin" title="Valet Status" navItems={NAV}>

      {/* Header */}
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#1565c0", marginBottom: 6 }}>HOA Admin</div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: 0 }}>Valet Status</h2>
            <p style={{ fontSize: 13, color: "var(--mut)", marginTop: 4 }}>
              {isToday ? "Live service tracking for your property." : "Historical service record."}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {isToday && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "#e3f2fd", color: "#1565c0", textTransform: "uppercase", letterSpacing: ".5px" }}>● Live</span>
            )}
            <DateNavigator date={selectedDate} onChange={setSelectedDate} />
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--mut)", fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          {/* Current Status Banner */}
          {currentStatus ? (() => {
            const cfg = getStatusConfig(currentStatus);
            return (
              <div className="fu d1" style={{ background: cfg.bg, border: `1.5px solid ${cfg.border}`, borderRadius: 14, padding: "18px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: cfg.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <cfg.icon size={20} color="#fff" strokeWidth={2} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--mut)", marginBottom: 4 }}>Current Status</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: cfg.color }}>{cfg.label}</div>
                  <div style={{ fontSize: 12.5, color: "var(--mut)", marginTop: 2 }}>{cfg.desc}</div>
                </div>
                {valetRecord?.updated_at && (
                  <div style={{ fontSize: 12, color: "var(--mut)", textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 600 }}>Last updated</div>
                    <div>{formatTime(valetRecord.updated_at)}</div>
                  </div>
                )}
              </div>
            );
          })() : (
            <div className="fu d1" style={{ background: "#fafafa", border: "1.5px solid var(--ln)", borderRadius: 14, padding: "18px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#bdbdbd", flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--mut)" }}>No Service {isToday ? "Today" : "This Day"}</div>
                <div style={{ fontSize: 12.5, color: "var(--mut)", marginTop: 2 }}>Valet has not started service yet.</div>
              </div>
            </div>
          )}

          {/* Status Timeline */}
          <div className="fu d2" style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "var(--mut)", marginBottom: 14 }}>
              Service Timeline
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {STATUS_STEPS.map((step, idx) => {
                const isDone   = currentIdx > idx;
                const isActive = currentIdx === idx;
                const isPending= currentIdx < idx;
                // Find timestamp from history
                const histEntry = statusHistory.find(h => h.metadata?.status === step.key);

                return (
                  <div key={step.key} style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
                    {/* Line + circle */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 32, flexShrink: 0 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                        background: isDone ? "var(--g600)" : isActive ? step.color : "#f0f0f0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: isActive ? `2px solid ${step.color}` : "none",
                        boxShadow: isActive ? `0 0 0 4px ${step.color}22` : "none",
                        zIndex: 1,
                      }}>
                        {isDone
                          ? <CheckCircle size={16} color="#fff" strokeWidth={2.5} />
                          : <step.icon size={14} color={isPending ? "#bdbdbd" : "#fff"} strokeWidth={2} />
                        }
                      </div>
                      {idx < STATUS_STEPS.length - 1 && (
                        <div style={{ width: 2, flex: 1, minHeight: 24, background: isDone ? "var(--g300)" : "#e0e0e0", margin: "4px 0" }} />
                      )}
                    </div>

                    {/* Content */}
                    <div style={{
                      flex: 1, paddingBottom: idx < STATUS_STEPS.length - 1 ? 20 : 0,
                      opacity: isPending ? 0.45 : 1,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, paddingTop: 5 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: isActive ? step.color : isDone ? "var(--g700)" : "var(--mut)" }}>
                            {step.label}
                            {isActive && isToday && (
                              <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: step.color, color: "#fff", textTransform: "uppercase", letterSpacing: ".5px", verticalAlign: "middle" }}>Active</span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--mut)", marginTop: 2 }}>{step.desc}</div>
                        </div>
                        {histEntry && (
                          <div style={{ fontSize: 12, color: "var(--mut)", fontWeight: 600, flexShrink: 0 }}>
                            {formatTime(histEntry.created_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary */}
          {statusHistory.length > 0 && (
            <div className="fu d3" style={{ background: "var(--g50)", border: "1px solid var(--g200)", borderRadius: 12, padding: "14px 18px", display: "flex", gap: 10, alignItems: "center" }}>
              <Clock size={14} color="var(--g600)" />
              <div style={{ fontSize: 12.5, color: "var(--ink2)", lineHeight: 1.6 }}>
                {statusHistory.length} status update{statusHistory.length > 1 ? "s" : ""} logged
                {statusHistory[0] && <span> · Started at <strong>{formatTime(statusHistory[0].created_at)}</strong></span>}
                {currentStatus === "completed" && statusHistory[statusHistory.length - 1] &&
                  <span> · Completed at <strong>{formatTime(statusHistory[statusHistory.length - 1].created_at)}</strong></span>
                }
              </div>
            </div>
          )}
        </>
      )}

      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </DashboardLayout>
  );
}